import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import type { FatiaDoRelatorio } from '@central/contracts';
import {
  Carregando,
  Contagem,
  Falhou,
  Numero,
  Painel,
  ResumoDoRecorte,
  type ColunaDeTabela,
} from '@/features/relatorios/componentes';
import { useFiltro } from '@/features/relatorios/filtros';
import { GraficoDeBarras } from '@/features/relatorios/graficos';
import { chavesDeRelatorio, obterReabertura } from '@/features/relatorios/relatorios.api';

/**
 * Taxa de reabertura — o indicador que separa "fechou o chamado" de "resolveu
 * o problema".
 *
 * O método de apuração vem do servidor e é exibido na tela, não escondido no
 * código: o contador de reaberturas não guarda quando cada uma aconteceu, e
 * quem for comparar dois meses precisa saber disso antes de comparar.
 */

const COLUNAS: ReadonlyArray<ColunaDeTabela<FatiaDoRelatorio>> = [
  { cabecalho: 'Equipe', celula: (l) => l.rotulo, csv: (l) => l.rotulo },
  {
    cabecalho: 'Reabertos',
    alinharADireita: true,
    celula: (l) => l.total.toLocaleString('pt-BR'),
    csv: (l) => l.total,
  },
];

export function ReaberturaPage() {
  const { consulta } = useFiltro();
  const relatorio = useQuery({
    queryKey: chavesDeRelatorio.de('reabertura', consulta),
    queryFn: () => obterReabertura(consulta),
  });

  if (relatorio.isPending) return <Carregando />;
  if (relatorio.isError) {
    return <Falhou erro={relatorio.error} aoTentar={() => void relatorio.refetch()} />;
  }

  const dados = relatorio.data;
  const taxa = dados.taxa.estado === 'OK' ? dados.taxa.valor : null;

  return (
    <div className="animate-slide-up space-y-6">
      <ResumoDoRecorte recorte={dados.recorte} />

      <div className="grid gap-3 sm:grid-cols-3">
        <Numero
          rotulo="Taxa de reabertura"
          indicador={dados.taxa}
          formatar={(valor) => `${(valor * 100).toFixed(1).replace('.', ',')}%`}
          tom={taxa === null ? 'neutro' : taxa > 0.05 ? 'ruim' : 'bom'}
        />
        <Contagem rotulo="Resolvidos no período" valor={dados.encerrados} />
        <Contagem
          rotulo="Com ao menos uma reabertura"
          valor={dados.reabertos}
          tom={dados.reabertos > 0 ? 'atencao' : 'bom'}
        />
      </div>

      <p className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
        <span>
          <span className="font-medium">Como é apurado:</span> {dados.metodo}
        </span>
      </p>

      <Painel
        titulo="Reaberturas por equipe"
        descricao="Concentração numa equipe costuma ser critério de encerramento diferente, não competência diferente."
        indicador={dados.porEquipe}
        grafico={(linhas) => (
          <GraficoDeBarras
            dados={linhas.map((l) => ({ rotulo: l.rotulo, total: l.total }))}
            nome="Reabertos"
          />
        )}
        colunas={() => COLUNAS}
        chaveDe={(linha) => linha.chave}
        nome={['relatorio', 'reabertura', 'por-equipe']}
        recorte={dados.recorte}
      />
    </div>
  );
}
